import { z } from "zod";
import { validateUKLicense } from "../utils";
import { ACCEPTED_IMAGE_TYPES, MAX_UPLOAD_SIZE, rnFileSchema } from "./file";

const vatStatuses = [
  "VAT_REGISTERED",
  "NOT_VAT_REGISTERED",
] as const;

const documentSchema = rnFileSchema
  .refine((file) => {
    if (typeof file === "object") {
      return !file || file.size <= MAX_UPLOAD_SIZE;
    }
    return true;
  }, `Max image size is 5MB.`)
  .refine((file) => {
    if (typeof file === "object") {
      return !file || ACCEPTED_IMAGE_TYPES.includes(file.type);
    }
    return true;
  }, "Only .jpg, .jpeg, .png, .pdf, and .webp formats are accepted.");

const driverFieldsSchema = z.object({
  company_name: z.string().optional(),
  legal_name: z.string().min(1, "Legal name is required for invoices"),
  trading_name: z.string().optional(),
  country: z.string().length(2).optional(),
  company_registration_number: z.string().optional(),
  vat_status: z.enum(vatStatuses, {
    message: "VAT status is required",
  }),
  vat_number: z.string().optional(),
  vat_effective_date: z.date().optional(),
  license_number: z
    .string()
    .min(1, "License number is required")
    .refine(validateUKLicense, {
      message: "Invalid UK driving license format",
    }),
  license_expiry_date: z.date({
    message: "License expiry date is required",
  }),
});

const withVatRefinement = <T extends z.ZodObject<z.ZodRawShape>>(schema: T) =>
  schema.superRefine((data, ctx) => {
    if (data.vat_status === "VAT_REGISTERED" && !data.vat_number) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "VAT number is required for VAT-registered operators",
        path: ["vat_number"],
      });
    }
    if (data.vat_status === "VAT_REGISTERED" && !data.vat_effective_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "VAT effective date is required for VAT-registered operators",
        path: ["vat_effective_date"],
      });
    }
  });

const baseDriverSchema = withVatRefinement(
  driverFieldsSchema.extend({ document: documentSchema }),
);

export const driverSchema = baseDriverSchema.refine((data) => {
  if (!data.document) {
    return false;
  }
  return true;
}, {
  message: "Document is required",
  path: ["document"],
});

export const updateDriverSchema = withVatRefinement(
  driverFieldsSchema.extend({ document: documentSchema.optional() }),
);

export type DriverFormData = z.infer<typeof driverSchema>;
export type UpdateDriverFormData = z.infer<typeof updateDriverSchema>;
export type RNFile = z.infer<typeof rnFileSchema>;
